import type { ConnectorStatus } from "~/ui-rtk/shared/types/connectors"
import type { CompanyConnectorCredentialGrantWarning } from "~/ui-rtk/api/types/company-connector.types"
import { serviceConnectorMap } from "~/ui-rtk/components/features/connectors/forms/utils"
import { getVersionMismatchWarning } from "~/ui-rtk/utils/connector-version-utils"

type ServiceConnectorConfig = {
  type: "connector" | "upload"
  component: unknown
  showForStatuses?: string[]
}

export interface ConnectorMetadataExtended {
  // Instagram specific
  igAccounts?: string[]
  facebookPages?: string[]

  // Google Keyword Search specific
  syncState?: {
    input?: {
      brand?: string
      regions?: string[]
      categories?: string[]
      competitors?: string[]
    }
  }

  // General sync info
  lastSyncDate?: string
  fullSyncCompleted?: boolean

  // Version and other metadata
  version?: number

  // Migration flags
  createdByMigration?: boolean
  migrationSource?: string

  // Set by the API when another company's Meta reconnect narrowed the shared
  // login so this connector's Instagram account is no longer covered.
  credentialGrantWarning?: CompanyConnectorCredentialGrantWarning | null

  [key: string]: unknown
}

export const getConnectorDescription = (
  serviceName: string,
  status: ConnectorStatus,
  metadata: ConnectorMetadataExtended | null | undefined,
  confirmedAt?: string,
  connectorName?: string,
  currentScopeVersion?: number,
  companyScopeVersion?: number,
  /**
   * Live-resolved account info (same payload `ConnectorInfo` modal uses).
   * When provided, takes precedence over inferring from `metadata` so the
   * card text matches what the user sees in the modal — e.g. for
   * instagram_insights this shows the `@username` of the picked igUserId.
   */
  accountInfo?: { accountName?: string; avatarUrl?: string; metric?: string },
  /**
   * True while the accountInfo query is in-flight. Lets the description
   * render a "Loading account…" placeholder instead of flashing the legacy
   * fallback before the live name arrives.
   */
  isAccountInfoLoading?: boolean,
  /**
   * True when the service has more than one CompanyConnector for this
   * company. When true the card can't pick a single account to spotlight,
   * so the description collapses to a generic "Connected".
   */
  hasMultipleConnectors?: boolean,
): string => {
  // Check if service has setup flow using imported serviceConnectorMap
  const connectorConfig: ServiceConnectorConfig | undefined =
    serviceConnectorMap[serviceName] as ServiceConnectorConfig | undefined
  const hasSetupFlow =
    connectorConfig?.showForStatuses &&
    connectorConfig.showForStatuses.length > 0

  // For services with setup flow, provide setup-focused descriptions
  if (hasSetupFlow && connectorConfig.showForStatuses?.includes(status)) {
    switch (status) {
      case "PENDING_SETUP":
        return "Setup incomplete - finish configuration"
      case "FAILED":
        return "Setup incomplete - finish configuration"
      case "RECONNECT":
        return "Setup incomplete - finish configuration"
    }
  }

  // Handle special NOT_CONNECTED case for services with setup flow
  if (hasSetupFlow && status === "NOT_CONNECTED") {
    // For OAuth-based services, mention authentication
    return "Click Connect to authenticate and configure"
  }

  // Default status descriptions
  if (status === "NOT_CONNECTED") {
    return "Click Connect to get started"
  }

  if (status === "PENDING") {
    return "Setting up connection..."
  }

  if (status === "PENDING_SETUP") {
    return "Setup incomplete - finish configuration"
  }

  if (status === "FAILED") {
    return "Connection failed - click to retry"
  }

  if (status === "UPGRADE") {
    // For UPGRADE status, always show version mismatch warning if available
    if (
      connectorName &&
      currentScopeVersion !== undefined &&
      companyScopeVersion !== undefined
    ) {
      return getVersionMismatchWarning(
        connectorName,
        currentScopeVersion,
        companyScopeVersion,
      )
    }
    return "New version available - upgrade to access latest features"
  }

  if (status === "RECONNECT") {
    return "Reconnection required"
  }

  // For connected services, try to extract meaningful account info
  if (status === "CONNECTED" && metadata) {
    // For services with setup flow, try to show specific connected info
    if (hasSetupFlow) {
      // GA4 - show property info if available
      if (serviceName === "ga4" && metadata.ga4Property) {
        return `Connected to GA4 property: ${metadata.ga4Property}`
      }
      // Add other setup-flow services here as needed
      return "Connected and configured"
    }

    // Instagram Insights — one connector binds to exactly one IG Business
    // Account. Prefer the live-resolved name (matches what the connector
    // info modal shows for the picked igUserId). While the accountInfo
    // query is in-flight show a placeholder. When the service has multiple
    // connections for this company the card can't spotlight one, so
    // collapse to plain "Connected". Always return — never fall through
    // to facebookPages / lastSync branches which would show a page list.
    if (serviceName === "instagram_insights") {
      if (hasMultipleConnectors) {
        return "Connected"
      }
      if (accountInfo?.accountName) {
        return `Connected: @${accountInfo.accountName}`
      }
      if (isAccountInfoLoading) {
        return "Loading account…"
      }
      return "Connected"
    }

    // Facebook Pages
    if (serviceName === "facebook_ads" && metadata.facebookPages) {
      const pages = metadata.facebookPages.slice(0, 2)
      const pageText = pages.join(", ")
      const moreText =
        metadata.facebookPages.length > 2
          ? ` +${metadata.facebookPages.length - 2} more`
          : ""
      return `Connected: ${pageText}${moreText}`
    }

    // Google Keyword Search
    if (
      serviceName.includes("google_keyword_search") &&
      metadata.syncState?.input?.brand
    ) {
      const brand = metadata.syncState.input.brand
      const regions = metadata.syncState.input.regions?.length || 0
      return `Tracking: ${brand} (${regions} region${regions !== 1 ? "s" : ""})`
    }

    // TikTok or other services - show last sync date if available
    if (metadata.lastSyncDate) {
      const syncDate = new Date(metadata.lastSyncDate)
      const now = new Date()
      const daysDiff = Math.floor(
        (now.getTime() - syncDate.getTime()) / (1000 * 60 * 60 * 24),
      )

      if (daysDiff === 0) {
        return "Last synced: Today"
      } else if (daysDiff === 1) {
        return "Last synced: Yesterday"
      } else if (daysDiff < 7) {
        return `Last synced: ${daysDiff} days ago`
      }
      return `Last synced: ${syncDate.toLocaleDateString()}`
    }

    // Fallback to confirmation date
    if (confirmedAt) {
      const confirmDate = new Date(confirmedAt)
      const now = new Date()
      const daysDiff = Math.floor(
        (now.getTime() - confirmDate.getTime()) / (1000 * 60 * 60 * 24),
      )

      if (daysDiff === 0) {
        return "Connected today"
      } else if (daysDiff === 1) {
        return "Connected yesterday"
      } else if (daysDiff < 7) {
        return `Connected ${daysDiff} days ago`
      }
      return `Connected ${confirmDate.toLocaleDateString()}`
    }

    return "Successfully connected"
  }

  return "Ready to connect"
}
